import { useMap } from "@vis.gl/react-maplibre";
import { useEffect, useRef } from "react";

// ─── Default config (overridable via props) ──────────────────────────────────

export interface ParticleConfig {
  /** Total particles drawn each frame. Higher = denser cover, more cost. */
  particleCount: number;
  /** Frames a particle lives before being respawned (0 = forever). */
  maxAge: number;
  /**
   * Visible trail length in CSS pixels. Each particle is drawn every frame
   * as a single gradient stroke from its tail (`trailLengthPx` upstream) to
   * its head (current position). Length is exact and INDEPENDENT of wind
   * speed — wind speed just moves the head faster across the canvas.
   */
  trailLengthPx: number;
  /** Particle stroke width in CSS pixels. */
  lineWidth: number;
  /**
   * How the line ends are drawn:
   *   "round"  — soft rounded cap (default; comet-like with tailAlpha=0).
   *   "butt"   — flat exactly at endpoints (crisp, arrow-like).
   *   "square" — flat extended by lineWidth/2 (slightly chunkier head).
   */
  lineCap: CanvasLineCap;
  /**
   * Alpha multiplier at the TAIL end of the gradient (0 = invisible tail
   * for a comet; 1 = solid line, no gradient). Head is always full alpha.
   */
  tailAlpha: number;
  /**
   * Linear pixel-per-frame motion per (m/s) of wind. Pure manual scaling — no
   * map-zoom or latitude factor. Tune visually for "looks like wind".
   */
  speedPxPerMs: number;
  /** Minimum per-frame pixel motion (calm-wind floor). 0 disables the floor. */
  minPxPerFrame: number;
  /** CSS colour string for every particle. Any valid CSS colour works. */
  color: string;
  /**
   * Frames at the start of a particle's life during which its stroke alpha
   * ramps from 0 → 1. Smooths the "pop-in" of newly spawned particles.
   * Set to 0 to disable the fade-in.
   */
  fadeInFrames: number;
  /**
   * Frames at the end of a particle's life during which its stroke alpha
   * ramps from 1 → 0. Smooths the "pop-out" before respawn at maxAge.
   * Set to 0 to disable the fade-out.
   */
  fadeOutFrames: number;
  /**
   * Per-particle lifespan jitter (0–1). Each particle is assigned its own
   * lifespan = maxAge × (1 ± jitter), uniformly random. 0 = all particles
   * use exactly maxAge (cycles synchronise → visible "wave" fades).
   * 0.5 = lifespans range from 50% to 150% of maxAge.
   */
  lifespanJitter: number;
}

// Production defaults — dialled in against the custom MapTiler dark style on
// the /particles-tune page. Values err on the subtle/sparse side: very few
// particles (60), short streaks (19 px), thick line (2.4 px), heavy desync
// via long fade-in/out and high lifespan jitter. Net effect: ambient,
// "occasional gust" feel rather than a busy weather radar.
export const DEFAULT_PARTICLE_CONFIG: ParticleConfig = {
  particleCount: 60,
  maxAge: 240,
  trailLengthPx: 19,
  lineWidth: 2.4,
  lineCap: "round",
  tailAlpha: 0,
  speedPxPerMs: 0.043,
  minPxPerFrame: 0.02,
  color: "rgba(160,171,253,0.55)",
  fadeInFrames: 143,
  fadeOutFrames: 152,
  lifespanJitter: 0.78,
};

// ─── Colour helpers ──────────────────────────────────────────────────────────

/** Parse a CSS rgba()/rgb()/#hex string into [r, g, b, a]. Returns white if unrecognised. */
function parseColor(css: string): [number, number, number, number] {
  const rgba = css.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/,
  );
  if (rgba) {
    return [
      Number(rgba[1]),
      Number(rgba[2]),
      Number(rgba[3]),
      rgba[4] !== undefined ? Number(rgba[4]) : 1,
    ];
  }
  const hex = css.match(/^#([0-9a-fA-F]{6})$/);
  if (hex) {
    const v = hex[1] ?? "";
    return [
      parseInt(v.slice(0, 2), 16),
      parseInt(v.slice(2, 4), 16),
      parseInt(v.slice(4, 6), 16),
      1,
    ];
  }
  return [255, 255, 255, 1];
}

// ─── Particle state (pixel-space, since the map is static) ───────────────────

interface Particle {
  x: number;
  y: number;
  age: number;
  /** This particle's individual lifespan in frames. Randomised at spawn so
   *  the population doesn't cycle in lockstep. */
  lifespan: number;
}

/** Pick a randomised lifespan around `maxAge` with `jitter` spread (0–1). */
function pickLifespan(maxAge: number, jitter: number): number {
  const j = Math.max(0, Math.min(1, jitter));
  if (j === 0) return maxAge;
  // Uniformly distributed in [maxAge*(1-j), maxAge*(1+j)], floor at 1
  const lo = maxAge * (1 - j);
  const hi = maxAge * (1 + j);
  return Math.max(1, lo + Math.random() * (hi - lo));
}

// ─── Component ───────────────────────────────────────────────────────────────

interface UniformWindParticlesProps {
  /** Meteorological direction (degrees, where the wind is coming FROM). */
  directionDeg: number | null;
  /** Wind speed in metres per second. */
  speedMs: number | null;
  /** Optional overrides for the visual parameters. Each field is independent. */
  config?: Partial<ParticleConfig>;
}

/**
 * Renders particles flowing in a single uniform direction over the map.
 * Used by the spot-detail map: one wind direction per hour, no grid sampling.
 *
 * Coordinate convention:
 *   directionDeg = compass direction the wind is *from* (meteorological).
 *   0° = north wind (blows south);   90° = east wind (blows west); etc.
 */
export function UniformWindParticles({
  directionDeg,
  speedMs,
  config,
}: UniformWindParticlesProps) {
  const { current: map } = useMap();
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);
  // Latest wind, stored as raw m/s velocity components so the per-frame
  // pixel conversion can use the map's CURRENT zoom & latitude (auto-rescale).
  const windRef = useRef<{ uMs: number; vMs: number }>({ uMs: 0, vMs: 0 });
  // Latest config — kept in a ref so live slider tweaks apply without re-creating the canvas.
  const cfgRef = useRef<ParticleConfig>({ ...DEFAULT_PARTICLE_CONFIG, ...config });
  cfgRef.current = { ...DEFAULT_PARTICLE_CONFIG, ...config };

  // Convert wind direction + speed into per-frame pixel velocity.
  // Screen y grows downward, so southward motion = +y.
  useEffect(() => {
    if (directionDeg == null || speedMs == null) {
      windRef.current = { uMs: 0, vMs: 0 };
      return;
    }
    // Convert "from" direction to "toward" velocity components (unit vector × speed).
    // u = east+ component (m/s), v = north+ component (m/s).
    const fromRad = (directionDeg * Math.PI) / 180;
    windRef.current = {
      uMs: -Math.sin(fromRad) * speedMs,
      vMs: -Math.cos(fromRad) * speedMs,
    };
  }, [directionDeg, speedMs]);

  // Set up the canvas overlay + rAF loop.
  useEffect(() => {
    if (!map) return;
    const mapInstance = map;

    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;top:0;left:0;pointer-events:none;z-index:2;";
    const container = mapInstance.getContainer();
    container.appendChild(canvas);

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.offsetWidth * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
      // Reseed particles to fill the new canvas
      const { particleCount, maxAge, lifespanJitter } = cfgRef.current;
      particlesRef.current = Array.from({ length: particleCount }, () => {
        const lifespan = pickLifespan(maxAge, lifespanJitter);
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          age: Math.floor(Math.random() * lifespan),
          lifespan,
        };
      });
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    const ctx = canvas.getContext("2d")!;

    function tick() {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;
      const cfg = cfgRef.current;

      // Per-frame pixel velocity from current wind (no zoom/lat factor)
      let dx = windRef.current.uMs * cfg.speedPxPerMs * dpr;
      let dy = -windRef.current.vMs * cfg.speedPxPerMs * dpr;

      // Always start a fresh frame — gradient lines are rendered fresh each
      // frame so we don't need any canvas trail-fade.
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, w, h);

      const rawLen = Math.sqrt(dx * dx + dy * dy);
      if (rawLen < 1e-3) {
        // No wind data → don't draw anything (parent component shows readouts).
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Apply calm-wind floor: scale velocity up to the minimum, preserving direction.
      const minPx = cfg.minPxPerFrame * dpr;
      if (rawLen < minPx) {
        const s = minPx / rawLen;
        dx *= s;
        dy *= s;
      }
      const effLen = Math.max(rawLen, minPx);

      // Tail offset = trailLengthPx upstream (i.e., opposite of motion).
      const trailPxScaled = cfg.trailLengthPx * dpr;
      const ux = -dx / effLen; // upstream unit vector x
      const uy = -dy / effLen;
      const tailDx = ux * trailPxScaled;
      const tailDy = uy * trailPxScaled;

      ctx.lineWidth = cfg.lineWidth * dpr;
      ctx.lineCap = cfg.lineCap;

      // Pre-parse colour so we can build per-particle gradients quickly.
      const [cr, cg, cb, ca] = parseColor(cfg.color);
      const tailA = ca * Math.max(0, Math.min(1, cfg.tailAlpha));
      const headA = ca;

      // Sync particle array length with cfg.particleCount when it changes live.
      if (particlesRef.current.length !== cfg.particleCount) {
        const cur = particlesRef.current;
        if (cfg.particleCount < cur.length) {
          cur.length = cfg.particleCount;
        } else {
          while (cur.length < cfg.particleCount) {
            const lifespan = pickLifespan(cfg.maxAge, cfg.lifespanJitter);
            cur.push({
              x: Math.random() * w,
              y: Math.random() * h,
              age: Math.floor(Math.random() * lifespan),
              lifespan,
            });
          }
        }
      }

      for (const p of particlesRef.current) {
        // Move particle and detect wrap-around
        let nx = p.x + dx;
        let ny = p.y + dy;
        const wrapped = nx < 0 || nx > w || ny < 0 || ny > h;

        if (!wrapped) {
          // Per-particle alpha envelope (cosine S-curve, see notes elsewhere).
          let fadeIn = Math.max(0, cfg.fadeInFrames);
          let fadeOut = Math.max(0, cfg.fadeOutFrames);
          const totalFade = fadeIn + fadeOut;
          if (totalFade > p.lifespan && totalFade > 0) {
            const k = p.lifespan / totalFade;
            fadeIn *= k;
            fadeOut *= k;
          }
          let envelope = 1;
          if (fadeIn > 0 && p.age < fadeIn) {
            const t = p.age / fadeIn;
            envelope = 0.5 - 0.5 * Math.cos(t * Math.PI);
          } else if (fadeOut > 0 && p.age > p.lifespan - fadeOut) {
            const t = (p.lifespan - p.age) / fadeOut;
            envelope = Math.max(0, 0.5 - 0.5 * Math.cos(t * Math.PI));
          }

          // Build gradient stroke: tail (upstream) → head (current pos).
          const tailX = nx + tailDx;
          const tailY = ny + tailDy;
          const grad = ctx.createLinearGradient(tailX, tailY, nx, ny);
          grad.addColorStop(0, `rgba(${cr},${cg},${cb},${tailA * envelope})`);
          grad.addColorStop(1, `rgba(${cr},${cg},${cb},${headA * envelope})`);
          ctx.strokeStyle = grad;

          ctx.beginPath();
          ctx.moveTo(tailX, tailY);
          ctx.lineTo(nx, ny);
          ctx.stroke();
        } else {
          // Wrap; skip drawing this frame to avoid a streak across the canvas.
          if (nx < 0) nx += w;
          else if (nx > w) nx -= w;
          if (ny < 0) ny += h;
          else if (ny > h) ny -= h;
        }

        p.x = nx;
        p.y = ny;
        p.age += 1;

        // Periodic respawn at the particle's individual lifespan.
        if (p.age > p.lifespan) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
          p.age = 0;
          p.lifespan = pickLifespan(cfg.maxAge, cfg.lifespanJitter);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      canvas.remove();
    };
  }, [map]);

  return null;
}
